/**
 * Expected entry fee of a payer — the ONE formula, run by the client card,
 * the admin fee panel, the registration callable and the post-guest-removal
 * recompute alike.
 *
 * A payer is the person the invoice goes to: a competitor who may also have
 * brought guests, or a parent who only registered guests. What they owe is
 * their own group's fee IF THEY COMPETE THEMSELVES, plus one group fee per
 * guest attached to them. A group without a fee override, and a competitor
 * without a group, pay the competition default.
 *
 * Until the 2026-09 audit follow-up (D1) the formula existed four times and
 * disagreed on one case: the client card charged a competitor WITHOUT a group
 * nothing (own fee keyed on having a group), while both server copies charged
 * the default (own fee keyed on competing). The server's rule is the one that
 * decides `entryFeePaid`, so it won; the ESM ↔ CommonJS pair that followed was
 * merged into this one copy in phase 1 of the shared-package plan.
 */

import { discountedAmount } from "./earlyBird.js";

/** `Number(x) || 0`, so a stored "3000" string or a null counts as money would. */
const money = (x) => Number(x) || 0;

/**
 * Events call this too, with `feeByGroup: {}` — an event fee is the same
 * model (own fee if taking part, one fee per guest) without group overrides.
 *
 * Each item is discounted and rounded on its own (`discountedAmount`), and the
 * total is the sum of the items: the export prints one line per item, and its
 * lines must add up to the total it prints. An item WITHOUT a discount keeps
 * its exact fee — a fractional 12.5 EUR fee stays 12.5, so paying exactly
 * that still settles it.
 *
 * @param {object} o
 * @param {number|string|null} o.defaultAmount  competition-level fee
 * @param {Object<string, number|string>} o.feeByGroup  groupId → override; a
 *   group absent here has no override. An explicit 0 IS an override (free group).
 * @param {boolean} o.competes  the payer themselves holds a competitor entry
 * @param {string|null} o.ownGroupId  the payer's own group, if any
 * @param {number} [o.ownDiscountPct]  early-bird % on the payer's own entry
 * @param {Array<{ groupId: string|null, discountPct?: number }>} [o.guests]
 *   one entry per guest, in order, each with the GUEST's own early-bird % —
 *   a guest added later earns the later tier, not the payer's
 * @param {Array<string|null>} [o.guestGroupIds]  legacy shape, read only when
 *   `guests` is absent; equals `guests` with a 0 % discount each
 * @returns {{ ownAmount: number, guestAmounts: number[], totalAmount: number,
 *   fullAmount: number, discountAmount: number }} `fullAmount` is the total
 *   without any discount, `discountAmount` = fullAmount − totalAmount — so a
 *   panel can show the total discount without a second formula
 */
export function computePayerFee({
  defaultAmount, feeByGroup, competes, ownGroupId, ownDiscountPct = 0, guests, guestGroupIds,
}) {
  const fallback = money(defaultAmount);
  const overrides = feeByGroup || {};
  const feeOf = (groupId) =>
    groupId && overrides[groupId] != null ? money(overrides[groupId]) : fallback;
  const guestList = Array.isArray(guests)
    ? guests.map((g) => ({ groupId: g ? g.groupId : null, discountPct: g ? g.discountPct : 0 }))
    : (guestGroupIds || []).map((groupId) => ({ groupId, discountPct: 0 }));
  const ownFull = competes ? feeOf(ownGroupId) : 0;
  const guestFull = guestList.map((g) => feeOf(g.groupId));
  const ownAmount = competes ? discountedAmount(ownFull, ownDiscountPct) : 0;
  const guestAmounts = guestList.map((g, i) => discountedAmount(guestFull[i], g.discountPct));
  const sum = (list) => list.reduce((acc, a) => acc + a, 0);
  const totalAmount = ownAmount + sum(guestAmounts);
  const fullAmount = ownFull + sum(guestFull);
  return { ownAmount, guestAmounts, totalAmount, fullAmount, discountAmount: fullAmount - totalAmount };
}

/**
 * Is the fee settled? Paid at least a positive total — OR the payer holds a
 * fee-bearing entry (`hasEntry`: competes / takes part, or has ≥1 guest) and
 * owes 0, because a 100 % early-bird tier or a free group must not drop a
 * competitor from the ranking as "unpaid". A payer without any entry who owes
 * nothing stays outside the fee altogether: not settled. Callers that pass no
 * options get the pre-2026-10 rule (0 owed is never settled).
 */
export function entryFeeSettled(paidAmount, totalAmount, { hasEntry } = {}) {
  const total = money(totalAmount);
  if (total > 0) return money(paidAmount) >= total;
  return total === 0 && hasEntry === true;
}

/** `{ groupId: entryFeeAmount }` from group documents, overrides only. */
export function feeByGroupFromDocs(groups) {
  const out = {};
  for (const g of groups || []) {
    const amount = g && g.entryFeeAmount;
    if (amount != null) out[g.id] = amount;
  }
  return out;
}
